import React, { useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, ArrowUpRight } from 'lucide-react';
import { api } from '@/api';
import { TID } from '@/testIds';

export default function InstallerJoin() {
  const [form, setForm] = useState({ name: '', email: '', city: '', state: '', years_experience: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/installers/signup', {
        name: form.name,
        email: form.email,
        city: form.city || undefined,
        state: form.state || undefined,
        years_experience: form.years_experience ? parseInt(form.years_experience, 10) : undefined,
      });
      toast.success("Thanks — we'll email you an invite to the Install Guild network.");
      setForm({ name: '', email: '', city: '', state: '', years_experience: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="installer-join" className="relative py-24 lg:py-28 border-b mi-line overflow-hidden">
      <div
        className="absolute inset-0 opacity-15 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/10126657/pexels-photo-10126657.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/70" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-12">
        <div>
          <div className="mi-eyebrow mb-3 text-[#E63946]">// FOR INSTALLERS</div>
          <h2 className="mi-headline text-5xl md:text-6xl mb-6">
            Wrap for a living?<br />
            <span className="text-[#E63946]">Join the guild.</span>
          </h2>
          <p className="text-white/75 leading-relaxed max-w-lg mb-6">
            Motor iNKS + Install Guild source jobs from print shops, sign companies and brands nationwide.
            You set your rate. We handle the intake, deposits and dispute cover.
          </p>
          <ul className="space-y-3 text-white/80 mb-6">
            <li className="flex items-start gap-3"><span className="mi-tag mi-tag-red text-[9px]">01</span> Steady B2B jobs — no chasing retail leads.</li>
            <li className="flex items-start gap-3"><span className="mi-tag mi-tag-red text-[9px]">02</span> Deposit protection on every gig.</li>
            <li className="flex items-start gap-3"><span className="mi-tag mi-tag-red text-[9px]">03</span> Free Wrapulator Pro seat while you're active.</li>
          </ul>
          <a href="https://www.installguild.pro" target="_blank" rel="noopener noreferrer" className="mi-btn-ghost">
            Learn more at installguild.pro <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <form onSubmit={submit} className="mi-surface p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <UserPlus className="w-5 h-5 text-[#E63946]" />
            <div className="mi-headline text-2xl">Apply as installer</div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <Input placeholder="Full name" required value={form.name} onChange={upd('name')} testid={TID.installerName} />
            <Input type="email" placeholder="Email" required value={form.email} onChange={upd('email')} testid={TID.installerEmail} />
            <div className="grid grid-cols-2 gap-4">
              <Input placeholder="City" value={form.city} onChange={upd('city')} testid={TID.installerCity} />
              <Input placeholder="State" value={form.state} onChange={upd('state')} testid={TID.installerState} />
            </div>
            <Input type="number" placeholder="Years of experience" min={0} max={60} value={form.years_experience} onChange={upd('years_experience')} testid={TID.installerYears} />
            <button
              type="submit"
              disabled={submitting}
              data-testid={TID.installerSubmit}
              className="mi-btn-red mt-2 disabled:opacity-60"
            >
              {submitting ? 'Submitting…' : 'Join the Guild'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Input({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25"
    />
  );
}
